"use client";

import {
  Map,
  AdvancedMarker,
  useMap,
  type MapCameraChangedEvent,
  type MapEvent,
} from "@vis.gl/react-google-maps";
import { RotateCw, Plus, Minus, Compass, MapPin } from "lucide-react";
import { MapPinIcon } from "@phosphor-icons/react";
import { MapErrorBoundary } from "@/components/map/MapErrorBoundary";
import {
  CategoryIconMapPin,
  MapPinDisc,
  MapPinTail,
} from "@/components/map/CategoryIconMapPin";
import {
  MapsApiProvider,
  GOOGLE_MAPS_MAP_ID,
} from "@/components/map/MapsApiProvider";
import { MarkerStyle } from "@/types/marker";
import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  memo,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { LoadingSpinner } from "@/components/ui/loading";
import { getLocationFromIP } from "@/lib/cities";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { getCategoryPhosphorIcon } from "@/components/home/variants/categoryPhosphorIcons";
import { searchIdentityFromUrl } from "@/lib/search/map-search-identity";
import { decideCamera } from "@/lib/search/map-camera";
import {
  hasNoMarkerInBounds,
  type ViewportBounds,
} from "@/lib/search/marker-visibility";
import { parseSearchUrl, transition, serializeSearchUrl } from "@/lib/search/search-state";
import { withAreaIdentity } from "@/lib/search/area-identity";
import {
  replaceUrlWithoutRouter,
  replaceSearchUrlShallow,
  liveSearchParams,
} from "@/lib/search/url-view-sync";
import { cn } from "@/lib/utils";
import {
  shouldExpandStationCluster,
  stationClusterRadiusKm,
} from "@/lib/mobility/station-cluster";
import { nearestPoints } from "@/lib/mobility/station-bounds";
import {
  baseProductId,
  clusterPoints,
  expansionZoom,
  groupMarkersBySpot,
  PRODUCT_CLUSTER_FLOOR_ZOOM,
  productClusterRadiusKm,
  spotKey,
  spotScopeOf,
  type SpotScope,
} from "@/lib/search/spot-cluster";
import { pickPinIconCodes } from "@/lib/search/pin-icons";
import {
  cameraAt,
  tweenDurationMs,
  type CameraPose,
} from "@/lib/search/camera-tween";

/** 마커가 놓인 자리의 좌표 키 — 선택 비교에 쓴다(spot-cluster.spotKey). */
const markerSpotKey = (m: { location: { lat: number; lng: number } }) =>
  spotKey(m.location.lat, m.location.lng);


/**
 * 핀을 누른 뒤 카메라를 맞추는 시점 — **지도 크기가 멈춘 뒤**.
 *
 * 모바일 지도는 드로어에 가려지는 게 아니라 드로어만큼 **줄어든다**(높이 100−드로어%,
 * 300ms 전환). 누르는 순간 fitBounds 하면 줄어들기 전 크기로 계산되고, 이어서 지도가
 * 줄면 Maps 는 중심만 지킨 채 위아래를 잘라 맞춰 둔 핀이 가장자리·화면 밖으로 밀렸다.
 * 그래서 크기 변화(ResizeObserver)가 멈출 때까지 기다린다 — 전환 시간을 하드코딩하지
 * 않으니 드래그 속도·기기와 무관하다.
 *   FIRST_WAIT: 크기가 변하기 시작하는지 지켜보는 시간(데스크톱은 이 뒤 바로 맞춘다)
 *   SETTLE:     마지막 크기 변화 뒤 이만큼 조용하면 멈춘 것으로 본다
 */
const CAMERA_FIRST_WAIT_MS = 80;
const CAMERA_SETTLE_MS = 120;

/** 핀을 누른 뒤 크기가 멈추면 할 카메라 동작 */
type PendingCamera =
  /** 끝까지 안 갈라지는 겹친 핀 — 목록을 열며 그 자리로 부드럽게 확대 */
  | { kind: "focus"; pose: CameraPose }
  /** 한 자리 핀 — 움직이지 않는다. 지도가 줄며 화면 밖으로 밀렸을 때만 보이게 옮긴다 */
  | { kind: "reveal"; point: google.maps.LatLngLiteral };


// 프로그램 이동(카메라 effect) 구간인지. 컴포넌트 밖에 두는 이유는 렌더 순수성
// 규칙(react-hooks/purity)이 컴포넌트 본문의 Date.now 호출을 막기 때문이다.
const isWithinProgrammaticMove = (until: number): boolean => Date.now() < until;


// Simplified marker type for map display
interface MapMarker {
  id: string | number;
  lat: number;
  lng: number;
  title: string;
  price: number;
  currency?: SupportedCurrency;
  image: string;
  location: { lat: number; lng: number; address: string };
  categoryCode?: string;
}

// Cluster type for grouped markers
interface Cluster {
  id: string;
  lat: number;
  lng: number;
  products: MapMarker[];
  count: number;
  originalProductIds: (string | number)[]; // 원본 상품 ID들
  /**
   * 이 덩어리가 덮는 **서로 다른 좌표**의 수 (product variant).
   *
   * 1이면 아무리 확대해도 갈라지지 않는다 — 같은 자리에 상품이 여럿인 경우다.
   * 그때는 확대 대신 목록을 연다(설계 §6 "갈라지면 확대, 더 못 가르면 목록").
   */
  spotCount?: number;
}

interface MapViewProps {
  products: MapMarker[];
  onMarkerHover?: (productId: string | number | null) => void;
  highlightedProductId?: string | number | null;
  markerStyle?: MarkerStyle;
  onMarkerStyleChange?: (style: MarkerStyle) => void;
  center?: { lat: number; lng: number }; // 지도 중심 좌표 (옵션)
  initialZoom?: number; // 초기 줌 레벨 (기본 12)
  isNearbySearch?: boolean; // 현재 위치 검색 중인지 여부
  onMapClick?: () => void; // 지도 클릭 시 호출되는 콜백
  /**
   * 카메라가 멈출 때마다 지도 중심·보이는 범위(09-28) — 지역·명소 필터가 "지금 보는 곳"을
   * 따라가게 한다. 드래그 중에는 부르지 않는다(idle 만).
   */
  onViewIdle?: (view: {
    center: { lat: number; lng: number };
    bounds: { ne_lat: number; ne_lng: number; sw_lat: number; sw_lng: number } | null;
  }) => void;
  locale?: string; // 지도 언어 설정
  /**
   * 상품 지도 — 한 자리(좌표)를 골랐을 때. 목록을 그 자리 상품으로 좁히는 데 쓴다.
   *
   * `productIds` 는 원본 상품 id(마커 접미사를 뗀 값)다. 지도 전체 필터
   * (`selectedCategories`)나 URL 은 건드리지 않는다 — 지도에서 고른 "범위"이지
   * 필터가 아니기 때문이다(설계 D5).
   */
  /**
   * 상품 지도 — 목록 범위로 고른 자리들의 좌표 키. 이 자리의 핀을 검정으로 반전시킨다.
   *
   * 클러스터 id(`cluster-N`)가 아니라 좌표 키다. 클러스터 번호는 줌·이동으로 다시
   * 묶일 때마다 바뀌어, 그걸로 기억하면 지도를 움직이는 순간 엉뚱한 핀이 켜진다.
   * 여러 자리가 묶인 핀을 고르면 키가 여럿이고, 확대 후 갈라진 핀이 모두 켜진다.
   * 멀리서 봐서 그 자리들이 덩어리에 묶이면 그 덩어리가 대신 켜진다.
   */
  selectedSpotKeys?: readonly string[];
  onSpotSelect?: (scope: SpotScope) => void;
  /** 카메라 fit 시 상단에 비워둘 px — 지도 위에 띄운 필터 알약에 마커가 가리지 않게 */
  cameraPaddingTop?: number;
  /**
   * 마커 어휘. 기본 "product" = 기존 동작.
   * "station" = 모빌리티 지점 허브(설계 §3-3). **핀 생김새는 product 와 동일**
   * (같은 Marker/ClusterMarker — 주황 디스크 + 검정 선 아이콘 + 가격 알약)이고,
   * 달라지는 건 네 가지뿐이다:
   *   1) 마커 id 를 branchId 그대로 쓴다(접미사 절단 없음)
   *   2) 클릭이 목록 범위(onSpotSelect) 대신 `onMarkerClick` / 클러스터 줌
   *   3) 클러스터 반경이 줌의 연속 함수(lib/mobility/station-cluster)
   *   4) 첫 진입 카메라를 전 지점 bounds 에 맞춘다
   * 아이콘은 소분류(50-01-02 스쿠터 / 50-01-05 오토바이)로 고르고, 핀에는 가격을
   * 넣지 않는다(한 지점에 차종이 여럿이라 금액이 어느 차의 값인지 말해 주지
   * 못한다 — 09-16 사용자 결정). 클러스터는 아이콘 + 지점 수.
   */
  variant?: "product" | "station";
  /** station variant 전용 — 핀/클러스터 클릭. 인자는 마커 id(=branchId) 원문. */
  onMarkerClick?: (id: string) => void;
  /** station variant 전용 — 반전(검정) 표시할 마커 id */
  selectedMarkerId?: string;
  /**
   * station variant 전용 — 목록 카드의 "지도" 버튼이 보낸 한 번짜리 요청(09-29).
   * 그 지점으로 이동·확대하고 핀을 선택 모양(검정)으로 띄우되 시트는 열지 않는다
   * (핀을 누르면 그때 연다). nonce 가 바뀔 때마다 다시 이동한다 — 같은 지점을 두 번
   * 눌러도 지도를 옮긴 뒤라면 다시 데려간다. 주소에는 쓰지 않는 순간 상태다.
   */
  focusStation?: { id: string; nonce: number } | null;
  /**
   * station variant 전용 — "핀이 보이게" 요청(09-29). 목록에는 지점이 있는데 지도 화면엔
   * 핀이 하나도 없을 수 있는 동작(빈 화면의 "전체 지점 보기"·필터 변경) 뒤에 허브가 보낸다.
   * 지금 지도 중심 + 가장 가까운 지점 5곳에 맞춘다(줌 상한 13). `ifNoneVisible` 이면 이미
   * 핀이 하나라도 보일 때는 그대로 둔다. 지도가 아직 0 크기면(모바일 드로어 펼침) 보일 때까지
   * 미룬다. 첫 진입·명소 이동 뒤에는 MapView 가 스스로 같은 판정을 한다.
   */
  revealRequest?: { nonce: number; mode: "always" | "ifNoneVisible" } | null;
  /**
   * station variant 전용 — 허브 명소 필터에서 고른 명소(09-23). 라벨 알약 + 검은 점으로
   * 그리고, 바뀔 때마다 그 자리로 이동한다(지점 선택과 같은 최소 줌).
   */
  landmark?: { lat: number; lng: number; label: string } | null;
  /**
   * "이 지역에서 검색" 결과를 받는 중(09-29). 버튼은 주소만 얕게 바꾸고 결과는 부모가
   * 받으므로, 버튼의 스피너는 부모가 알려 준다. 결과를 받지 않는 허브는 넘기지 않는다.
   */
  areaSearchPending?: boolean;
}

/** station variant — 지점을 고르면 최소 이 줌까지 당긴다(12 ≈ 화면 폭 10km, 도시 안 위치를 파악하는 정도 — 09-18 사용자 결정). */
const STATION_FOCUS_ZOOM = 12;

/**
 * station variant — 카드의 "지도" 버튼은 그 지점 **자체**를 보러 가는 것이라 더 당긴다
 * (15 ≈ 화면 폭 1.2km, 주변 길·역이 보이는 정도). 이미 더 가까우면 그대로 둔다.
 */
const STATION_REVEAL_ZOOM = 15;

/** station variant — "핀이 보이게" 맞출 때의 줌 상한(가까운 지점 하나뿐이어도 동네가 보이게). */
const STATION_REVEAL_MAX_ZOOM = 13;

// Category Avatar component for markers (matches CategoryFilterDialog icon style)
const CategoryAvatar = ({
  categoryCode,
  size = "sm",
  selected = false,
  preciseIcon = false,
}: {
  categoryCode?: string;
  size?: "sm" | "md";
  selected?: boolean;
  /**
   * 소분류(L3) 매핑을 그대로 쓴다 — 지점 허브 전용. 기본값 false 는 아래 주석의
   * 중분류 고정 규칙(상품 지도 동작)이며 바뀌지 않는다.
   */
  preciseIcon?: boolean;
}) => {
  // 아이콘은 카테고리 필터와 같은 매핑을 쓰되 **중분류로 고정**한다("10-01-03" 드론 →
  // 카메라·드론). 소분류 매핑(예: 자전거)까지 따르면 같은 "스쿠터·바이크" 안에서
  // 상품마다 아이콘이 갈라져 지도에서 한 종류로 읽히지 않는다. 중분류 매핑이 없으면
  // getCategoryPhosphorIcon 이 대분류로 폴백한다.
  // 지점 허브는 반대로 소분류가 필요하다: 화면 전체가 한 중분류("차·스쿠터·
  // 바이크")뿐이라 중분류로 고정하면 전 핀이 자동차 아이콘이 된다.
  const iconCode = categoryCode
    ? preciseIcon
      ? categoryCode
      : categoryCode.split("-").slice(0, 2).join("-")
    : "";
  return (
    <MapPinDisc
      icon={getCategoryPhosphorIcon(iconCode)}
      selected={selected}
      size={size === "sm" ? 28 : 32}
    />
  );
};

// Marker bubble — wrapped by <AdvancedMarker> at the call site for positioning
interface MarkerProps {
  product: MapMarker;
  markerStyle: MarkerStyle;
  selected: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  /** 지점 허브 전용 — 아이콘을 소분류로 고른다 (CategoryAvatar 주석 참고) */
  preciseIcon?: boolean;
  /**
   * 지점 허브 전용 — 가격 없이 아이콘만 그린다. 지점 핀이 말해야 하는 건 "여기서
   * 빌릴 수 있다"이고, 한 지점에 차종이 여러 개라 핀 위의 금액은 어느 차의 값인지
   * 알 수 없다. 기본 false = 상품 지도의 가격 알약 그대로.
   */
  hidePrice?: boolean;
}

const Marker = ({
  product,
  selected,
  onMouseEnter,
  onMouseLeave,
  onClick,
  preciseIcon = false,
  hidePrice = false,
}: MarkerProps) => {
  const { formatFromSource } = useCurrency();
  const formattedPrice = formatFromSource(
    product.price,
    product.currency || "KRW",
  );
  // `hidePrice` 로만 갈린다 — 상품 지도는 가격이 0이어도 지금까지 그리던 대로
  // 그려야 하므로(동작 불변) 값으로 추가 판단하지 않는다.
  const showPrice = !hidePrice;

  if (!showPrice) {
    return (
      <div
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          fontFamily:
            '"Pretendard Variable", ui-sans-serif, system-ui, sans-serif',
        }}
        className="cursor-pointer transition-all duration-200 hover:z-50 hover:drop-shadow-[0_6px_14px_rgba(0,0,0,0.34)]"
      >
        <CategoryIconMapPin
          categoryCode={product.categoryCode}
          selected={selected}
          preciseIcon={preciseIcon}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily:
          '"Pretendard Variable", ui-sans-serif, system-ui, sans-serif',
      }}
      className="flex flex-col items-center transition-all duration-200 hover:scale-105 drop-shadow-[0_2px_6px_rgba(0,0,0,0.30)] hover:drop-shadow-[0_6px_14px_rgba(0,0,0,0.34)] hover:z-50"
    >
      {/* Bubble */}
      <div
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={cn(
          "relative cursor-pointer py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap",
          "pl-0.5 pr-2.5",
          selected
            ? "bg-neutral-900 text-white"
            : "bg-white text-gray-900",
        )}
      >
        <CategoryAvatar
          categoryCode={product.categoryCode}
          size="md"
          selected={selected}
          preciseIcon={preciseIcon}
        />
        <span className="font-semibold text-[13px]">{formattedPrice}</span>
      </div>
      {/* 꼬리 — 알약 아래에 붙인 SVG, 끝이 상자 바닥(= 마커 앵커) */}
      <MapPinTail
        className={selected ? "text-neutral-900" : "text-white"}
      />
    </div>
  );
};

// Cluster Marker component for grouped products
interface ClusterMarkerProps {
  cluster: Cluster;
  selected: boolean;
  onClick: () => void;
  label: string;
  /** 지점 허브 전용 — 아이콘을 소분류로 고른다 (CategoryAvatar 주석 참고) */
  preciseIcon?: boolean;
  /**
   * 핀에 금액을 적지 않는다. 한 자리에 상품이 여럿(중앙값 15)이라 금액은 "최저가~"
   * 밖에 될 수 없고, 그러면 어느 상품 값인지 말해 주지 못한다 — 안 적는 편이 정확하다.
   */
  hidePrice?: boolean;
  /**
   * 덩어리에 **지점(좌표) 수**를 "N곳"으로 적는다 — 허브·일반 검색 공통(10-01).
   * 단위를 붙여 "무슨 수지?"를 없앴다(설계 D3·D9 의 숫자 금지를 단위로 대체).
   */
  showCount?: boolean;
}

const ClusterMarker = ({
  cluster,
  selected,
  onClick,
  preciseIcon = false,
  hidePrice = false,
  showCount = false,
}: ClusterMarkerProps) => {
  const { convertFromSource, formatFromSource } = useCurrency();
  const t = useTranslations("search");

  // 클러스터 아바타는 중분류 단위로 고유하게 — 같은 대분류라도 "카메라·드론"과
  // "캠핑"은 다른 아이콘으로 보이게 한다(색은 대분류라 같다).
  const uniqueCategories = useMemo(() => {
    const codes = new Set<string>();
    cluster.products.forEach((p) => {
      if (p.categoryCode) {
        codes.add(
          preciseIcon
            ? p.categoryCode
            : p.categoryCode.split("-").slice(0, 2).join("-"),
        );
      }
    });
    return Array.from(codes);
  }, [cluster.products, preciseIcon]);

  /**
   * 핀에 그릴 아이콘 — **그림 종류만큼, 최대 3장**(lib/search/pin-icons).
   *
   * 같은 종류가 여럿이면 1장이다. 한때 묶인 수만큼 같은 아이콘을 반복해 "여기는
   * 하나가 아니다"를 표시했지만, 한 자리에 같은 종류 상품이 여럿인 핀까지 3장으로
   * 겹쳐 보여 "같은 카테고리인데 왜 셋이냐"가 됐다(09-23 사용자 피드백). 종류는
   * 코드가 아니라 **그림**으로 가른다 — 코드가 달라도 그림이 같은 소분류가 있다.
   */
  const visibleCategories = useMemo(
    () => pickPinIconCodes(uniqueCategories, getCategoryPhosphorIcon),
    [uniqueCategories],
  );


  // 같은 카테고리만 모인 클러스터는 아이콘 하나 + 최저가 "~". 섞였으면 아이콘만
  // 최대 3개를 12px 겹쳐 "여기 여러 종류가 있다"만 말하고, 개수·가격은 모달이 맡는다
  // (개수 배지는 6월 도입, 09-15 제거). 최저가는 통화가 섞일 수 있어(KRW·JPY) 화면
  // 통화로 환산해 고른 뒤, 그 상품의 원 통화 가격을 기존 표시 경로로 보여준다.
  const isSingleCategory = uniqueCategories.length === 1;
  const lowestPrice = useMemo(() => {
    if (hidePrice) return null;
    if (!isSingleCategory || cluster.products.length === 0) return null;
    let cheapest = cluster.products[0];
    let cheapestValue = convertFromSource(
      cheapest.price,
      (cheapest.currency || "KRW") as SupportedCurrency,
    );
    for (const p of cluster.products.slice(1)) {
      const v = convertFromSource(
        p.price,
        (p.currency || "KRW") as SupportedCurrency,
      );
      if (v < cheapestValue) {
        cheapest = p;
        cheapestValue = v;
      }
    }
    return formatFromSource(
      cheapest.price,
      (cheapest.currency || "KRW") as SupportedCurrency,
    );
  }, [
    hidePrice,
    isSingleCategory,
    cluster.products,
    convertFromSource,
    formatFromSource,
  ]);

  const hasText = Boolean(lowestPrice) || showCount;

  return (
    <div
      style={{
        fontFamily:
          '"Pretendard Variable", ui-sans-serif, system-ui, sans-serif',
      }}
      className="flex flex-col items-center transition-all duration-200 drop-shadow-[0_2px_6px_rgba(0,0,0,0.30)] hover:scale-105 hover:drop-shadow-[0_6px_14px_rgba(0,0,0,0.34)] hover:z-50"
    >
      {/* Bubble */}
      <div
        onClick={onClick}
        className={cn(
          "relative cursor-pointer py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap",
          hasText ? "pl-0.5 pr-2.5" : "px-0.5",
          // 글자가 있는 알약만 선택 시 검정으로 뒤집는다. 아이콘뿐인 핀은 알약을
          // 흰색으로 두고 원만 검정으로 — CategoryIconMapPin 과 같은 선택 표시.
          selected && hasText
            ? "bg-neutral-900 text-white"
            : "bg-white text-gray-900",
        )}
      >
        <div className="flex items-center -space-x-3">
          {visibleCategories.map((code) => (
            <CategoryAvatar
              key={code}
              categoryCode={code}
              size="md"
              selected={selected}
              preciseIcon={preciseIcon}
            />
          ))}
        </div>
        {lowestPrice && (
          <span className="font-semibold text-[13px]">{lowestPrice}~</span>
        )}
        {showCount && (
          // 숫자만 두면 "무슨 수지?"가 된다 — 단위(곳·spots·か所…)를 붙인다(10-01 B안).
          // 단위는 숫자보다 한 단계 작고 얇게.
          <span className="font-semibold text-[13px] tabular-nums">
            {t.rich("stationClusterCount", {
              // 상품 지도의 count 는 상품 수라, 핀에는 좌표(지점) 수를 쓴다.
              count: cluster.spotCount ?? cluster.count,
              u: (chunks) => (
                <span className="ml-px text-[12px] font-medium">{chunks}</span>
              ),
            })}
          </span>
        )}
      </div>
      {/* 꼬리 — 알약 아래에 붙인 SVG, 끝이 상자 바닥(= 마커 앵커) */}
      <MapPinTail
        className={selected && hasText ? "text-neutral-900" : "text-white"}
      />
    </div>
  );
};

// Current Location Marker — wrapped by <AdvancedMarker anchorPoint={CENTER}>
// at the call site so the inner dot lands exactly on the coordinate.
// Negative margins from the legacy google-map-react era were removed because
// AdvancedMarker now handles anchoring; keeping them caused the dot to drift.
const CurrentLocationMarker = () => {
  return (
    <div className="relative h-6 w-6 flex items-center justify-center">
      {/* Outer pulse ring */}
      <div className="absolute inset-0 bg-blue-500/30 rounded-full animate-ping" />
      {/* Middle ring */}
      <div className="absolute inset-1 bg-blue-500/20 rounded-full" />
      {/* Inner dot */}
      <div className="relative h-4 w-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
    </div>
  );
};

// Helper that captures the underlying google.maps.Map instance from
// @vis.gl's Map context and forwards it to a parent state setter.
function MapInstanceCapture({
  onReady,
}: {
  onReady: (map: google.maps.Map | null) => void;
}) {
  const map = useMap();
  useEffect(() => {
    onReady(map ?? null);
  }, [map, onReady]);
  return null;
}

function MapView({
  products,
  onMarkerHover,
  highlightedProductId,
  markerStyle = "price",
  onMarkerStyleChange,
  center: providedCenter,
  initialZoom,
  isNearbySearch = false,
  onMapClick,
  onViewIdle,
  locale = "ko",
  onSpotSelect,
  selectedSpotKeys,
  cameraPaddingTop = 0,
  variant = "product",
  onMarkerClick,
  selectedMarkerId,
  focusStation = null,
  revealRequest = null,
  landmark = null,
  areaSearchPending = false,
}: MapViewProps) {
  // station variant 는 마커 id 를 branchId 로 쓰므로 접미사 절단·모달을 타지 않는다.
  const isStationVariant = variant === "station";
  const searchParams = useSearchParams();
  const t = useTranslations("search");
  // 사용자가 실제로 지도를 움직였는가. 프로그램 이동(카메라 effect)은 제외한다.
  const [hasMapMoved, setHasMapMoved] = useState(false);
  const [currentMapCenter, setCurrentMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [currentMapBounds, setCurrentMapBounds] = useState<{
    ne_lat: number;
    ne_lng: number;
    sw_lat: number;
    sw_lng: number;
  } | null>(null);
  // station variant — "낱개로 펼친" 지점 id. 클러스터를 눌러도 더 갈라지지 않는
  // 덩어리를 여기에 담아 클러스터링에서 제외한다(무한 확대 방지, 설계 §3-3).
  const [expandedStationIds, setExpandedStationIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [zoom, setZoom] = useState(initialZoom ?? 12);
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  // URL에서 현재 bounds 파라미터 가져오기
  const urlNeLat = searchParams.get("ne_lat");
  const urlNeLng = searchParams.get("ne_lng");
  const urlSwLat = searchParams.get("sw_lat");
  const urlSwLng = searchParams.get("sw_lng");

  // 지도 view 상태(zoom/center) — 검색 bounds(ne/sw)와 분리. pan/zoom 시 replaceState로
  // 갱신하여 새로고침/뒤로가기 시 보던 위치 복원(결과 refetch와 무관).
  const urlViewZoom = searchParams.get("zoom");
  const urlClat = searchParams.get("clat");
  const urlClng = searchParams.get("clng");
  // pan/zoom URL 반영 디바운스 타이머
  const viewSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 프로그램 이동(카메라 effect)이 만들어 내는 camera_changed 는 "사용자가 지도를
  // 움직였다"로 세지 않는다. 이동 직전에 여기에 만료 시각을 찍고, 지도가 멈추면
  // (onIdle) 바로 푼다. 1500ms 는 애니메이션이 끝나지 못한 경우의 상한일 뿐이다.
  const programmaticMoveUntilRef = useRef(0);
  // 줌 변화 감지용 — 중심이 거의 그대로여도 줌만 바꾸면 이동으로 본다.
  const lastZoomRef = useRef<number | null>(null);
  // 이동 거리를 재는 기준점. center prop(URL·기본값)은 지도가 프로그램 이동으로
  // 자리를 잡은 뒤에도 그대로라, 드로어 리사이즈·주소창 접힘처럼 제스처 없는
  // bounds 변화에도 버튼이 떠 버린다. 그래서 "마지막으로 정착한 중심"을 따로 둔다.
  const anchorCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  // "이게 새 검색인가" 판단 키 — pathname|scope|q. view 동기화(zoom/clat/clng)나
  // 카테고리 필터 변경으로는 바뀌지 않는다(map-search-identity.ts 주석 참고).
  const pathname = usePathname();
  const searchIdentity = searchIdentityFromUrl(pathname, searchParams);

  // Use provided center if valid, otherwise use default
  // Don't calculate from products to avoid unnecessary re-renders
  const providedCenterValid =
    providedCenter?.lat != null &&
    providedCenter?.lng != null &&
    !isNaN(providedCenter.lat) &&
    !isNaN(providedCenter.lng);

  // Default center (Seoul)
  const defaultCenter = { lat: 37.5665, lng: 126.978 };

  // URL view center(clat/clng)가 있으면 최우선 → 새로고침 시 보던 위치 복원
  // (useMemo: 아래 카메라 effect의 deps라서 매 렌더 새 객체면 effect가 계속 재실행된다)
  const urlViewCenter = useMemo(
    () =>
      urlClat && urlClng && !isNaN(parseFloat(urlClat)) && !isNaN(parseFloat(urlClng))
        ? { lat: parseFloat(urlClat), lng: parseFloat(urlClng) }
        : null,
    [urlClat, urlClng],
  );
  const center = urlViewCenter ?? (providedCenterValid ? providedCenter : defaultCenter);
  const initialViewZoom = urlViewZoom ? parseFloat(urlViewZoom) : initialZoom ?? 12;

  // URL의 강제 이동 신호(t). 지도 이동 판단은 아래 카메라 effect가 전담한다.
  const urlTimestamp = searchParams.get("t");

  // 언마운트 시 view 동기화 디바운스 타이머 정리
  useEffect(() => {
    return () => {
      if (viewSyncTimer.current) clearTimeout(viewSyncTimer.current);
    };
  }, []);

  // 지도 카메라 결정은 decideCamera(map-camera.ts) 한 곳에서만 한다.
  // 카메라 키: 검색 정체성 + 강제 이동 신호. view 동기화(zoom/clat/clng)·카테고리
  // pushState는 키를 바꾸지 않는다. t는 같은 검색을 다시 눌러도(도쿄 카드 2회)
  // 값이 바뀌므로 포함한다.
  /**
   * 그릴 수 있는 마커 좌표만 — 초기 카메라와 "지점이 있는 곳으로" 버튼이 **같은
   * 목록**을 봐야 한다(버튼은 보이는데 눌러도 안 움직이는 상태 방지).
   */
  const markerPoints = useMemo(
    () =>
      products
        .filter(
          (p) =>
            p.location?.lat != null &&
            p.location?.lng != null &&
            !isNaN(p.location.lat) &&
            !isNaN(p.location.lng),
        )
        .map((p) => ({ lat: p.location.lat, lng: p.location.lng })),
    [products],
  );

  /**
   * 전 마커에 카메라를 맞춘다. 초기 station 카메라와 **같은 패딩 규칙**을 쓴다 —
   * 상단은 지도 위에 띄운 필터 알약(cameraPaddingTop) 만큼 더 비운다.
   */
  const fitAllMarkers = useCallback(() => {
    if (!mapInstance || markerPoints.length === 0) return;
    if (markerPoints.length === 1) {
      mapInstance.setCenter(markerPoints[0]);
      mapInstance.setZoom(15);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    markerPoints.forEach((point) => bounds.extend(point));
    mapInstance.fitBounds(bounds, {
      top: 60 + cameraPaddingTop,
      right: 40,
      bottom: 40,
      left: 40,
    });
  }, [mapInstance, markerPoints, cameraPaddingTop]);

  /**
   * idle 시점의 viewport — 드래그 **중**에는 갱신하지 않는다.
   * onCameraChanged 는 팬 도중 연속으로 터져 버튼이 깜빡이므로 idle 만 본다.
   */
  const [idleBounds, setIdleBounds] = useState<ViewportBounds | null>(null);
  const handleMapIdle = useCallback(
    (event: MapEvent) => {
      const bounds = event.map.getBounds();
      setIdleBounds(bounds ? bounds.toJSON() : null);
      const center = event.map.getCenter();
      if (center) {
        const b = bounds?.toJSON();
        onViewIdle?.({
          center: { lat: center.lat(), lng: center.lng() },
          bounds: b ? { ne_lat: b.north, ne_lng: b.east, sw_lat: b.south, sw_lng: b.west } : null,
        });
      }
    },
    [onViewIdle],
  );

  /**
   * "핀은 있는데 화면엔 하나도 없다" — 한국에서 "현 위치"를 누르면 일본 지점이
   * 전부 화면 밖으로 나가 지도를 손으로 끌어 되돌아오는 수밖에 없었다.
   * idle bounds 와 마커 목록에서 **파생**한다(별도 state 없음) — 검색 결과가
   * 바뀌어 마커가 갈아끼워져도 카메라를 안 움직인 채 바로 다시 판정된다.
   */
  const markersOffscreen = useMemo(
    () => hasNoMarkerInBounds(markerPoints, idleBounds),
    [markerPoints, idleBounds],
  );

  // ===== station variant: 지점 선택 → 그 지점을 지도 가운데로 + 줌 당김 =====
  // 선택이 바뀔 때 한 번 이동하고, 그 뒤 지도 div 크기가 바뀌면(모바일 시트를 내려
  // 지도가 다시 드러날 때) 사용자가 아직 지도를 손대지 않은 동안만 같은 지점으로
  // 다시 맞춘다 — Google Maps 는 컨테이너가 커질 때 중심을 유지하지 않는다.
  // 의존성은 좌표 문자열이다: 지점 상세(이름·사진)가 늦게 도착해 products 가
  // 바뀌어도 좌표가 같으면 다시 움직이지 않는다.
  const selectedStationPointKey = useMemo(() => {
    if (!isStationVariant || !selectedMarkerId) return null;
    const target = products.find(
      (p) => String(p.id) === String(selectedMarkerId),
    );
    const lat = target?.location?.lat;
    const lng = target?.location?.lng;
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;
    return `${lat},${lng}`;
  }, [isStationVariant, selectedMarkerId, products]);
  const userMovedSinceFocusRef = useRef(false);
  // 첫 카메라·명소 이동 뒤 첫 정착(idle)에서 한 번 "핀이 보이나"를 본다(onIdle 이 소비,
  // revealNearestStations). 사용자가 지도를 잡으면 취소 — 사용자가 옮긴 화면을 뒤집지 않는다.
  const autoRevealPendingRef = useRef(false);

  useEffect(() => {
    if (!mapInstance || !selectedStationPointKey) return;
    const [lat, lng] = selectedStationPointKey.split(",").map(Number);
    const point = { lat, lng };
    userMovedSinceFocusRef.current = false;

    const focus = () => {
      programmaticMoveUntilRef.current = Date.now() + 1500;
      const currentZoom = mapInstance.getZoom() ?? 0;
      if (currentZoom < STATION_FOCUS_ZOOM) {
        mapInstance.setZoom(STATION_FOCUS_ZOOM);
      }
      mapInstance.panTo(point);
      // 지도 위 필터 알약이 핀을 가리지 않게, 그만큼 아래(보이는 영역의 가운데)로 둔다
      if (cameraPaddingTop > 0) mapInstance.panBy(0, -cameraPaddingTop / 2);
    };
    focus();

    const div = mapInstance.getDiv();
    const observer = new ResizeObserver(() => {
      if (userMovedSinceFocusRef.current) return;
      const rect = div.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      focus();
    });
    observer.observe(div);
    return () => observer.disconnect();
  }, [mapInstance, selectedStationPointKey, cameraPaddingTop]);

  // 명소를 고르면 그 자리로 — 좌표 문자열이 바뀔 때만 한 번(라벨만 바뀌면 그대로).
  const landmarkPointKey = landmark ? `${landmark.lat},${landmark.lng}` : null;
  useEffect(() => {
    if (!mapInstance || !landmarkPointKey) return;
    const [lat, lng] = landmarkPointKey.split(",").map(Number);
    programmaticMoveUntilRef.current = Date.now() + 1500;
    if ((mapInstance.getZoom() ?? 0) < STATION_FOCUS_ZOOM) {
      mapInstance.setZoom(STATION_FOCUS_ZOOM);
    }
    mapInstance.panTo({ lat, lng });
    if (cameraPaddingTop > 0) mapInstance.panBy(0, -cameraPaddingTop / 2);
    // 명소 둘레에 지점이 없으면 정착 뒤 가까운 지점까지 넓힌다(아래 onIdle)
    autoRevealPendingRef.current = true;
  }, [mapInstance, landmarkPointKey, cameraPaddingTop]);

  const cameraKey = `${searchIdentity}|${urlTimestamp ?? ""}`;
  const hasLandmark = !!landmark;
  const appliedCameraKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // 지도 준비 전이면 키를 소비하지 않는다 — mapInstance가 deps에 있어 준비되는
    // 렌더에서 다시 들어와 그때 이동한다.
    if (!mapInstance) return;
    if (appliedCameraKeyRef.current === cameraKey) return;
    appliedCameraKeyRef.current = cameraKey;
    // 새 검색(정체성·강제 이동 신호 변경)이면 "이 지역에서 검색" 버튼을 다시 숨긴다.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset when the search identity changes
    setHasMapMoved(false);
    // station variant: 이 카메라가 정착한 뒤 핀이 하나도 안 보이면(검색한 지역·명소에 지점이
    // 없음) 가까운 지점까지 넓힌다 — 목록은 지점을 보여 주는데 지도는 비는 상태 방지(09-29).
    if (isStationVariant) autoRevealPendingRef.current = true;

    // 카메라를 코드가 움직이기 직전에 찍는다 — handleMapChange 가 이 구간의
    // camera_changed 를 사용자 이동으로 세지 않는다. "keep"은 카메라를 건드리지
    // 않으므로 찍지 않는다.
    const markProgrammaticMove = () => {
      programmaticMoveUntilRef.current = Date.now() + 1500;
    };

    // station variant 첫 진입(URL view 없음 · 내 주변 검색 아님)은 **전 지점**에
    // 맞춘다. decideCamera 는 ne/sw bounds 가 있으면 t 없이는 카메라를 그대로
    // 두는데(규칙 ③), 지점 허브는 검색 bounds 와 무관하게 "지금 예약 가능한 지점이
    // 어디까지 퍼져 있는지"를 먼저 보여 줘야 한다.
    // 단, 검색바로 지역·명소를 찾아 들어온 경우(범위 + 강제 이동 t)와 명소가 있는 경우는
    // 그 범위로 간다(09-28) — 안 그러면 "서울역"·"도쿄"로 찾아도 일본 전역이 보였다.
    const searchedArea = !!urlTimestamp && !!(urlNeLat && urlNeLng && urlSwLat && urlSwLng);
    if (
      isStationVariant &&
      !urlViewCenter &&
      !urlViewZoom &&
      !isNearbySearch &&
      !searchedArea &&
      !hasLandmark &&
      markerPoints.length > 0
    ) {
      markProgrammaticMove();
      fitAllMarkers();
      return;
    }

    const action = decideCamera({
      urlBounds:
        urlNeLat && urlNeLng && urlSwLat && urlSwLng
          ? {
              ne_lat: parseFloat(urlNeLat),
              ne_lng: parseFloat(urlNeLng),
              sw_lat: parseFloat(urlSwLat),
              sw_lng: parseFloat(urlSwLng),
            }
          : null,
      hasForceMove: !!urlTimestamp,
      urlViewCenter,
      markers: products.map((p) => ({ lat: p.lat, lng: p.lng })),
      placeCenter: providedCenterValid && providedCenter ? providedCenter : null,
      placeZoom: initialZoom,
    });

    switch (action.type) {
      case "keep":
        return;
      case "fitBounds": {
        const b = action.bounds;
        console.log("🗺️ 지도를 URL bounds로 이동 (timestamp 있음):", b);
        markProgrammaticMove();
        mapInstance.fitBounds(
          new google.maps.LatLngBounds(
            { lat: b.sw_lat, lng: b.sw_lng }, // SW corner
            { lat: b.ne_lat, lng: b.ne_lng }, // NE corner
          ),
          { top: cameraPaddingTop, right: 0, bottom: 0, left: 0 },
        );
        return;
      }
      case "fitMarkers": {
        // "한 지점만 보여줄 때"의 줌.
        //
        // 지역별 줌은 이미 fitBounds가 알아서 한다 — 마커가 흩어져 있으면 그 퍼짐에
        // 맞춰 서울은 넓게, 홍대는 좁게 잡힌다. 이 상수가 개입하는 건 퍼짐이 0인
        // 경우(상품 1개, 또는 전 상품이 같은 좌표)뿐이고, 그때는 애초에 지역 크기를
        // 유추할 정보가 없다. 그래서 여기서 물어야 할 건 "이 지역이 얼마나 넓은가"가
        // 아니라 "가게 한 곳을 볼 때 얼마나 당겨야 하는가"이며, 상수가 정답이다.
        //
        // 16 ≈ 화면 폭 600m — 가게 위치가 분명하면서 주변 거리·지하철 맥락이 남는다.
        // (점 크기 bounds에 fitBounds를 걸면 ~21까지 들어가므로 캡 역할도 겸한다.)
        const SINGLE_LOCATION_ZOOM = 16;
        if (action.markers.length === 1) {
          markProgrammaticMove();
          mapInstance.setCenter(action.markers[0]);
          mapInstance.setZoom(SINGLE_LOCATION_ZOOM);
        } else {
          const bounds = new google.maps.LatLngBounds();
          action.markers.forEach((m) => bounds.extend(m));
          markProgrammaticMove();
          mapInstance.fitBounds(bounds, {
            top: 50 + cameraPaddingTop,
            right: 50,
            bottom: 50,
            left: 50,
          });
          google.maps.event.addListenerOnce(mapInstance, "idle", () => {
            const currentZoom = mapInstance.getZoom();
            if (currentZoom !== undefined && currentZoom > SINGLE_LOCATION_ZOOM) {
              // onIdle 이 이미 플래그를 풀었으므로 이 보정 줌 직전에 다시 찍는다.
              markProgrammaticMove();
              mapInstance.setZoom(SINGLE_LOCATION_ZOOM);
            }
          });
        }
        return;
      }
      case "center":
        markProgrammaticMove();
        mapInstance.setCenter(action.center);
        mapInstance.setZoom(action.zoom);
        return;
    }
  }, [
    hasLandmark,
    mapInstance,
    cameraKey,
    urlNeLat,
    urlNeLng,
    urlSwLat,
    urlSwLng,
    urlTimestamp,
    urlViewCenter,
    products,
    markerPoints,
    fitAllMarkers,
    providedCenterValid,
    providedCenter,
    initialZoom,
    cameraPaddingTop,
    isStationVariant,
    urlViewZoom,
    isNearbySearch,
  ]);

  // ===== station variant: 카드의 "지도" 버튼 → 그 지점으로 이동·확대(09-29) =====
  // 위 카메라 effect **뒤에** 둔다 — 태블릿처럼 지도가 이 요청과 함께 새로 마운트되면
  // 첫 카메라(전 지점 맞춤)가 먼저 돌고 이 이동이 이긴다. 모바일은 드로어가 내려가며
  // 지도 div 가 자라므로, 지점 선택과 같이 사용자가 손대기 전까지 크기 변화마다 다시 맞춘다.
  // 키에 nonce 를 넣어 같은 지점을 다시 눌러도 다시 데려간다.
  const focusStationKey = useMemo(() => {
    if (!isStationVariant || !focusStation) return null;
    const target = products.find((p) => String(p.id) === focusStation.id);
    const lat = target?.location?.lat;
    const lng = target?.location?.lng;
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;
    return `${lat},${lng}#${focusStation.nonce}`;
  }, [isStationVariant, focusStation, products]);

  useEffect(() => {
    if (!mapInstance || !focusStationKey) return;
    const [lat, lng] = focusStationKey.split("#")[0].split(",").map(Number);
    const point = { lat, lng };
    userMovedSinceFocusRef.current = false;

    const focus = () => {
      programmaticMoveUntilRef.current = Date.now() + 1500;
      if ((mapInstance.getZoom() ?? 0) < STATION_REVEAL_ZOOM) {
        mapInstance.setZoom(STATION_REVEAL_ZOOM);
      }
      mapInstance.panTo(point);
      if (cameraPaddingTop > 0) mapInstance.panBy(0, -cameraPaddingTop / 2);
    };
    const div = mapInstance.getDiv();
    const rect = div.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) focus();

    const observer = new ResizeObserver(() => {
      if (userMovedSinceFocusRef.current) return;
      const next = div.getBoundingClientRect();
      if (next.width === 0 || next.height === 0) return;
      focus();
    });
    observer.observe(div);
    return () => observer.disconnect();
  }, [mapInstance, focusStationKey, cameraPaddingTop]);

  /**
   * 지금 지도 화면에 핀이 하나도 없으면(또는 `always`) "지금 중심 + 가까운 지점 5곳"에
   * 맞춘다 — 중심을 넣어 두어 사용자가 보던 자리가 화면에서 사라지지 않는다. 줌은 13 까지만.
   * 판정을 끝냈으면 true, 아직 할 수 없으면(지도 없음·0 크기·핀 없음) false — 호출자가 미룬다.
   */
  const revealNearestStations = useCallback(
    (mode: "always" | "ifNoneVisible"): boolean => {
      if (!mapInstance || markerPoints.length === 0) return false;
      const rect = mapInstance.getDiv().getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
      const c = mapInstance.getCenter();
      if (!c) return false;
      const view = mapInstance.getBounds();
      if (
        mode === "ifNoneVisible" &&
        view &&
        markerPoints.some((point) => view.contains(point))
      ) {
        return true;
      }
      const center = { lat: c.lat(), lng: c.lng() };
      const target = new google.maps.LatLngBounds();
      target.extend(center);
      nearestPoints(center, markerPoints).forEach((point) => target.extend(point));
      programmaticMoveUntilRef.current = Date.now() + 1500;
      mapInstance.fitBounds(target, {
        top: 60 + cameraPaddingTop,
        right: 40,
        bottom: 40,
        left: 40,
      });
      google.maps.event.addListenerOnce(mapInstance, "idle", () => {
        const zoomNow = mapInstance.getZoom();
        if (zoomNow !== undefined && zoomNow > STATION_REVEAL_MAX_ZOOM) {
          programmaticMoveUntilRef.current = Date.now() + 1500;
          mapInstance.setZoom(STATION_REVEAL_MAX_ZOOM);
        }
      });
      return true;
    },
    [mapInstance, markerPoints, cameraPaddingTop],
  );

  // 허브가 보낸 "핀이 보이게" 요청 — nonce 당 한 번. 핀·지도 크기가 준비될 때까지 미룬다
  // (주소가 바뀐 뒤 마커가 다음 렌더에 오면 revealNearestStations 가 바뀌어 다시 들어온다).
  const appliedRevealNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isStationVariant || !revealRequest || !mapInstance) return;
    const { nonce, mode } = revealRequest;
    if (appliedRevealNonceRef.current === nonce) return;
    const attempt = () => {
      if (appliedRevealNonceRef.current === nonce) return;
      if (revealNearestStations(mode)) appliedRevealNonceRef.current = nonce;
    };
    attempt();
    if (appliedRevealNonceRef.current === nonce) return;
    const observer = new ResizeObserver(attempt);
    observer.observe(mapInstance.getDiv());
    return () => observer.disconnect();
  }, [isStationVariant, revealRequest, mapInstance, revealNearestStations]);

  // ── 카메라를 부드럽게 옮긴다 (lib/search/camera-tween)
  //
  // fitBounds·setZoom 은 순간 이동이라 겹친 핀을 눌렀을 때 "확" 확대됐다. 검색 지도는
  // 소수 줌을 쓰는 벡터 지도라 프레임마다 moveCamera 로 조금씩 옮긴다. 사용자가 지도를
  // 잡으면(드래그) 그 자리에서 멈춘다. 동작 줄이기 설정이면 바로 옮긴다.
  const tweenFrameRef = useRef<number | null>(null);

  const cancelCameraTween = useCallback(() => {
    if (tweenFrameRef.current !== null) {
      cancelAnimationFrame(tweenFrameRef.current);
      tweenFrameRef.current = null;
    }
  }, []);

  const animateCamera = useCallback(
    (to: CameraPose) => {
      if (!mapInstance) return;
      cancelCameraTween();

      const center = mapInstance.getCenter();
      const zoomNow = mapInstance.getZoom();
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!center || zoomNow === undefined || reduceMotion) {
        mapInstance.moveCamera({
          center: { lat: to.lat, lng: to.lng },
          zoom: to.zoom,
        });
        return;
      }

      const from: CameraPose = {
        lat: center.lat(),
        lng: center.lng(),
        zoom: zoomNow,
      };
      const duration = tweenDurationMs(from.zoom, to.zoom);
      let startedAt: number | null = null;

      const step = (now: number) => {
        // `??=` 는 쓰지 않는다 — React 컴파일러 린트가 이 문법에서 컴포넌트 분석을
        // 통째로 포기해 다른 훅 진단까지 가려진다(09-23 확인).
        if (startedAt === null) startedAt = now;
        const t = (now - startedAt) / duration;
        const pose = cameraAt(from, to, t);
        mapInstance.moveCamera({
          center: { lat: pose.lat, lng: pose.lng },
          zoom: pose.zoom,
        });
        tweenFrameRef.current = t < 1 ? requestAnimationFrame(step) : null;
      };
      tweenFrameRef.current = requestAnimationFrame(step);
    },
    [mapInstance, cancelCameraTween],
  );

  useEffect(() => cancelCameraTween, [cancelCameraTween]);

  /**
   * 상품 지도의 겹친 핀 클릭.
   *
   * - 여러 자리가 묶인 핀: **목록은 열지 않고, 핀이 갈라지는 줌까지만 부드럽게 확대**
   *   한다(09-23 사용자 요청). 갈라지는 줌은 지도와 같은 규칙(clusterPoints)으로
   *   계산한다 — 누를 때마다 한 겹씩 풀린다.
   * - 반경 하한(50m)보다 붙어 끝까지 안 갈라지는 자리들: 반경이 멈추는 줌(16)까지
   *   확대하고 목록을 연다. 그러지 않으면 눌러도 아무 일 없는 핀이 된다.
   * - 한 자리 핀: 그 자리 상품 목록을 연다. 모달은 없다 — 목록 카드가 카테고리를
   *   보여 주므로 "무엇을 볼지" 한 번 더 묻지 않는다.
   */
  const handleClusterClick = (cluster: Cluster) => {
    if ((cluster.spotCount ?? 1) > 1 && mapInstance) {
      const spots = groupMarkersBySpot(cluster.products).map((spot) => ({
        lat: spot.lat,
        lng: spot.lng,
      }));
      const bounds = new google.maps.LatLngBounds();
      spots.forEach((pt) => bounds.extend(pt));
      const center = bounds.getCenter().toJSON();
      // 지도가 클러스터링에 쓰는 줌과 같은 값(Math.round(zoom)). 아래쪽의 clusterZoom 을
      // 여기서 참조하면 React 컴파일러가 선언 전 참조로 보고 클러스터링 useMemo 최적화를
      // 건너뛴다 — 같은 식을 여기서 직접 쓴다.
      const splitZoom = expansionZoom(spots, Math.round(zoom));

      if (splitZoom !== null) {
        animateCamera({ ...center, zoom: splitZoom });
        return;
      }

      onSpotSelect?.(spotScopeOf(cluster.products));
      requestCamera({
        kind: "focus",
        pose: {
          ...center,
          zoom: Math.max(mapInstance.getZoom() ?? 0, PRODUCT_CLUSTER_FLOOR_ZOOM),
        },
      });
      return;
    }

    onSpotSelect?.(spotScopeOf(cluster.products));
    requestCamera({
      kind: "reveal",
      point: { lat: cluster.lat, lng: cluster.lng },
    });
  };

  // ── 핀 선택 뒤 카메라: 지도 크기가 멈춘 뒤 맞춘다 (CAMERA_* 주석 참고)
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingCameraRef = useRef<PendingCamera | null>(null);
  const cameraTimerRef = useRef<number | undefined>(undefined);

  const applyPendingCamera = useCallback(() => {
    const job = pendingCameraRef.current;
    pendingCameraRef.current = null;
    if (!job || !mapInstance) return;

    if (job.kind === "focus") {
      animateCamera(job.pose);
      return;
    }

    const visible = mapInstance.getBounds();
    if (visible && !visible.contains(job.point)) {
      mapInstance.panTo(job.point);
    }
  }, [mapInstance, animateCamera]);

  const scheduleCamera = useCallback(
    (delayMs: number) => {
      window.clearTimeout(cameraTimerRef.current);
      cameraTimerRef.current = window.setTimeout(applyPendingCamera, delayMs);
    },
    [applyPendingCamera],
  );

  const requestCamera = (job: PendingCamera) => {
    pendingCameraRef.current = job;
    scheduleCamera(CAMERA_FIRST_WAIT_MS);
  };

  // 지도 크기가 바뀌는 동안은 맞추기를 계속 미룬다 — 멈춘 뒤 한 번만 맞춘다.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (pendingCameraRef.current) scheduleCamera(CAMERA_SETTLE_MS);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      window.clearTimeout(cameraTimerRef.current);
    };
  }, [scheduleCamera]);

  // Get API key from environment variable
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY || "";


  // Clustering algorithm - group nearby markers based on zoom level
  /**
   * 클러스터 재계산 시점 — **정수 줌**으로만 센다.
   *
   * onCameraChanged 는 줌 애니메이션 매 프레임 터지고, 줌 12.3 → 12.7 사이에
   * 묶임이 바뀔 이유는 없다. 소수점을 그대로 의존성에 두면 한 번의 핀치 줌이
   * 클러스터링을 수십 번 다시 돌린다 — 이것이 "확대하면 렉" 의 직접 원인이다.
   */
  const clusterZoom = Math.round(zoom);

  /**
   * 뷰포트 컬링 박스 — 현재 화면을 20% 넓힌 영역.
   *
   * 화면 밖 핀은 DOM 을 만들지 않는다. 여유분을 두는 이유는 드래그할 때 가장자리에서
   * 핀이 갑자기 나타나지 않게 하기 위해서다. bounds 를 아직 모르면(첫 렌더) 컬링을
   * 건너뛴다 — 그때 걸러 버리면 첫 화면이 비어 보인다.
   */
  const cullBounds = useMemo(() => {
    if (!currentMapBounds) return null;
    const { ne_lat, ne_lng, sw_lat, sw_lng } = currentMapBounds;
    const padLat = Math.abs(ne_lat - sw_lat) * 0.2;
    const padLng = Math.abs(ne_lng - sw_lng) * 0.2;
    return {
      north: ne_lat + padLat,
      south: sw_lat - padLat,
      east: ne_lng + padLng,
      west: sw_lng - padLng,
    };
  }, [currentMapBounds]);

  // Clustering algorithm - group nearby markers based on zoom level
  const { clusters, singleMarkers } = useMemo(() => {
    // Clustering radius based on zoom level (in km).
    // 두 variant 모두 줌의 연속 함수를 쓴다 — 기존 상수 계단(0.05/0.1/0.2km)은 나라
    // 단위 줌에서 사실상 묶이지 않아 도쿄 위에 핀 수백 개가 겹쳤다.
    const clusterRadius = isStationVariant
      ? stationClusterRadiusKm(clusterZoom)
      : productClusterRadiusKm(clusterZoom);

    const clustered: Cluster[] = [];
    const single: MapMarker[] = [];

    // Only process products with valid location data
    const productsWithLocation = products.filter(
      (p) =>
        p.location?.lat != null &&
        p.location?.lng != null &&
        !isNaN(p.location.lat) &&
        !isNaN(p.location.lng),
    );

    // ── C1: 같은 좌표는 핀 하나 (product variant 전용)
    //
    // 마커는 `상품 × 지점`이라 지점 하나에 상품 15개(중앙값)가 정확히 같은 좌표로
    // 쌓인다. 화면에는 원래 맨 위 하나만 보였으므로 합쳐도 보이는 핀 수는 그대로이고,
    // 거리 계산 입력만 4,427 → 269 로 줄어든다.
    //
    // station variant 는 이미 마커 하나가 지점 하나라 그대로 둔다.
    type Node = {
      lat: number;
      lng: number;
      markers: MapMarker[];
    };
    const nodes: Node[] = isStationVariant
      ? productsWithLocation.map((p) => ({
          lat: p.location.lat,
          lng: p.location.lng,
          markers: [p],
        }))
      : groupMarkersBySpot(productsWithLocation).map((spot) => ({
          lat: spot.lat,
          lng: spot.lng,
          markers: spot.markers,
        }));

    // ── 뷰포트 컬링 (product variant 전용)
    //
    // station variant 는 첫 진입 카메라를 전 지점 bounds 에 맞추는 동작이 있어
    // 화면 밖 지점도 알아야 한다.
    const visibleNodes =
      isStationVariant || !cullBounds
        ? nodes
        : nodes.filter(
            (n) =>
              n.lat <= cullBounds.north &&
              n.lat >= cullBounds.south &&
              n.lng <= cullBounds.east &&
              n.lng >= cullBounds.west,
          );

    // 선택된 지점과 이미 펼친 지점은 반경 안에 있어도 자기 핀으로 남는다 —
    // 시트가 열려 있는 지점이 클러스터에 삼켜지면 "지금 보고 있는 곳"이 지도에서
    // 사라진다.
    const keepAlone = new Set<number>();
    if (isStationVariant) {
      visibleNodes.forEach((node, index) => {
        const id = String(node.markers[0].id);
        if (
          id === String(selectedMarkerId) ||
          id === focusStation?.id ||
          expandedStationIds.has(id)
        ) {
          keepAlone.add(index);
          single.push(node.markers[0]);
        }
      });
    }

    // ── C2: 줌에 비례한 반경으로 스팟들을 다시 묶는다.
    // 규칙은 clusterPoints 하나 — 겹친 핀을 눌렀을 때 "몇 줌에서 갈라지나"를 계산하는
    // expansionZoom 도 같은 함수를 쓴다. 둘이 다르면 계산상 갈라지는 줌까지 확대했는데
    // 지도에선 안 갈라지는 핀이 생긴다.
    clusterPoints(visibleNodes, clusterRadius, keepAlone).forEach((indices) => {
      const group = indices.map((i) => visibleNodes[i]);
      const groupMarkers = group.flatMap((n) => n.markers);

      // 낱개 핀은 "좌표 하나 + 상품 하나"일 때만이다. 같은 자리에 상품이 여럿이면
      // 디스크를 겹쳐 "여기는 하나가 아니다"를 말하고, 눌렀을 때 목록을 연다.
      if (group.length === 1 && groupMarkers.length === 1) {
        single.push(groupMarkers[0]);
        return;
      }

      const avgLat = group.reduce((sum, n) => sum + n.lat, 0) / group.length;
      const avgLng = group.reduce((sum, n) => sum + n.lng, 0) / group.length;

      // Extract unique original product IDs from the markers
      // Marker IDs are in format "uuid-index", so extract the base UUID
      // (station variant 의 id 는 branchId 라 접미사 절단을 적용하지 않는다)
      const productIds = [
        ...new Set(
          groupMarkers.map((p) => {
            const id = String(p.id);
            if (isStationVariant) return id;
            // Remove the branch index suffix (e.g., "uuid-0" -> "uuid")
            const lastDashIndex = id.lastIndexOf("-");
            // Check if the suffix after last dash is a number (branch index)
            if (lastDashIndex !== -1) {
              const suffix = id.substring(lastDashIndex + 1);
              if (/^\d+$/.test(suffix)) {
                return id.substring(0, lastDashIndex);
              }
            }
            return id;
          }),
        ),
      ];

      clustered.push({
        id: `cluster-${clustered.length}`,
        lat: avgLat,
        lng: avgLng,
        products: groupMarkers,
        count: isStationVariant ? group.length : groupMarkers.length,
        originalProductIds: productIds,
        spotCount: group.length,
      });
    });

    return { clusters: clustered, singleMarkers: single };
  }, [
    products,
    clusterZoom,
    cullBounds,
    isStationVariant,
    selectedMarkerId,
    focusStation?.id,
    expandedStationIds,
  ]);

  /**
   * station variant 핀을 선택 모양(검정)으로 그리는가 — 시트가 열린 지점, 카드의 "지도"
   * 버튼으로 찾아간 지점, 데스크톱에서 마우스를 올린 카드의 지점(highlightedProductId).
   */
  const isStationEmphasized = (id: string | number) => {
    const key = String(id);
    return (
      key === String(selectedMarkerId) ||
      key === focusStation?.id ||
      (highlightedProductId != null && key === String(highlightedProductId))
    );
  };

  // 목록 범위로 고른 자리들 — 핀마다 비교하므로 집합으로 한 번 만든다.
  const selectedSpotKeySet = useMemo(
    () => new Set(selectedSpotKeys ?? []),
    [selectedSpotKeys],
  );

  const handleSearchCurrentLocation = () => {
    // 뷰 파라미터(zoom/clat/clng)는 라우터를 거치지 않고 주소창에만 쓰이므로
    // useSearchParams 가 아니라 "지금 주소창"에서 읽는다(url-view-sync).
    const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
    const go = (bounds: { ne_lat: number; ne_lng: number; sw_lat: number; sw_lng: number }) => {
      // 범위만 바꾸고 q·필터는 유지, place·뷰는 버린다(spec §4 searchThisArea).
      // 경로 이름도 옮긴 범위의 도시로(area-identity) — 서울에서 도쿄로 와도 /s/seoul 로 남지 않게.
      const next = withAreaIdentity(transition(current, { type: "searchThisArea", bounds }));
      // 명소로 검색해 들어왔으면(?place=) 그 명소 선택을 lm 으로 옮겨 둔다(09-29) —
      // searchThisArea 는 place 를 버리므로 그대로 두면 명소 칩·핀이 풀리고, 상품 상세에
      // 갔다 뒤로 와도 복원되지 않았다. 필터처럼 URL 이 정본이어야 한다.
      if (current.identity.place && !next.passthrough.lm) {
        next.passthrough = { ...next.passthrough, lm: current.identity.place };
      }
      // 지금 보고 있는 중심·줌을 뷰로 싣는다(decideCamera ①: clat → keep). 얕은 이동이라
      // 지도가 다시 만들어지지는 않지만, 새로고침·상품 상세에서 뒤로 왔을 때 이 자리로
      // 복원되고, 허브 지도가 "첫 진입 = 전 지점 맞춤"으로 착각하지 않게 한다.
      const c = mapInstance?.getCenter();
      const z = mapInstance?.getZoom();
      if (c && z != null) {
        next.view = {
          clat: Math.round(c.lat() * 1e5) / 1e5,
          clng: Math.round(c.lng() * 1e5) / 1e5,
          zoom: Math.round(z),
        };
      }
      // 주소만 얕게 바꾼다(09-29) — router.replace 는 페이지 전체를 서버에서 다시 그리고,
      // 경로 이름이 바뀌면 세그먼트가 새로 마운트돼 스켈레톤·지도 재생성이 났다. 핀·목록은
      // 부모(SearchPageClient)가 바뀐 주소를 보고 결과만 받아 갈아 끼운다. 지도는 검색
      // 페이지에만 있으므로 항상 replace — 검색 세션은 히스토리 한 칸.
      replaceSearchUrlShallow(serializeSearchUrl(locale, next));
      // 다음 이동 판정의 기준점을 "지금 보고 있는 중심"으로 옮긴다 — 그러지 않으면
      // 이전 기준점에서 이미 멀어져 있어 버튼이 곧바로 다시 뜬다.
      anchorCenterRef.current = currentMapCenter;
      setHasMapMoved(false);
    };

    // 버튼은 handleMapChange 가 중심·bounds 를 채운 뒤에만 뜨므로 둘 중 하나는 반드시 있다.
    if (currentMapBounds) {
      go(currentMapBounds);
    } else if (currentMapCenter) {
      const offset = 0.03;
      go({
        ne_lat: currentMapCenter.lat + offset,
        ne_lng: currentMapCenter.lng + offset,
        sw_lat: currentMapCenter.lat - offset,
        sw_lng: currentMapCenter.lng - offset,
      });
    }
  };

  if (!apiKey) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-gray-500">{t("map.errors.mapsApiKeyRequired")}</p>
        </div>
      </div>
    );
  }

  const handleMapChange = (ev: MapCameraChangedEvent) => {
    const { center: newCenter, zoom: newZoom, bounds } = ev.detail;

    // 현재 지도 중심 좌표 저장
    setCurrentMapCenter(newCenter);
    setZoom(newZoom);

    // bounds 정보 저장 (에어비앤비 스타일 검색용)
    if (bounds) {
      setCurrentMapBounds({
        ne_lat: bounds.north,
        ne_lng: bounds.east,
        sw_lat: bounds.south,
        sw_lng: bounds.west,
      });
    }

    // "사용자가 실제로 움직였는가" 판정 — 프로그램 이동(카메라 effect)이 만든
    // camera_changed 는 제외한다. 중심이 100m 이상 이동했거나 줌이 바뀌면 이동으로 본다.
    const programmatic = isWithinProgrammaticMove(
      programmaticMoveUntilRef.current,
    );
    const anchor = anchorCenterRef.current ?? center;
    const distance = calculateMapDistance(
      anchor.lat,
      anchor.lng,
      newCenter.lat,
      newCenter.lng,
    );
    const zoomChanged =
      lastZoomRef.current !== null && lastZoomRef.current !== newZoom;
    lastZoomRef.current = newZoom;
    if (!programmatic && (distance > 0.1 || zoomChanged)) {
      setHasMapMoved(true);
      // 지점 포커스 이후 사용자가 지도를 움직였다 — 시트 크기가 바뀌어도 되돌리지 않는다
      userMovedSinceFocusRef.current = true;
    }

    // 지도 view(zoom/center)를 URL에 반영 — 디바운스 후 replaceState.
    // router.push가 아닌 history.replaceState라 Next 네비게이션/refetch를 유발하지 않고,
    // 뒤로가기 히스토리도 늘리지 않는다. 검색 bounds(ne/sw)는 건드리지 않는다.
    // 사용자가 직접 pan한 뒤엔 강제 fitBounds용 t= 를 제거해 새로고침 시 보던 위치를 유지.
    if (viewSyncTimer.current) clearTimeout(viewSyncTimer.current);
    viewSyncTimer.current = setTimeout(() => {
      if (typeof window === "undefined") return;
      const params = new URLSearchParams(window.location.search);
      params.set("zoom", String(Math.round(newZoom)));
      params.set("clat", newCenter.lat.toFixed(5));
      params.set("clng", newCenter.lng.toFixed(5));
      params.delete("t");
      // Next 라우터를 거치면 진행 중인 router.push(이 지역 검색·필터)가 버려진다.
      replaceUrlWithoutRouter(
        `${window.location.pathname}?${params.toString()}`,
      );
    }, 400);
  };

  // 현재 위치 가져오기 (GPS 실패 시 IP fallback)
  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);

    const setLocationAndMoveMap = (latitude: number, longitude: number) => {
      setCurrentLocation({ lat: latitude, lng: longitude });
      setIsGettingLocation(false);

      // 지도를 현재 위치로 이동
      if (mapInstance) {
        mapInstance.setCenter({ lat: latitude, lng: longitude });
      }
    };

    const tryIPFallback = async () => {
      console.log("📍 GPS 실패, IP 기반 위치로 fallback 시도...");
      try {
        const ipLocation = await getLocationFromIP();
        if (ipLocation.latitude && ipLocation.longitude) {
          console.log(
            `📍 IP 기반 위치: ${ipLocation.latitude}, ${ipLocation.longitude}`,
          );
          setLocationAndMoveMap(ipLocation.latitude, ipLocation.longitude);
        } else {
          console.error("📍 IP 기반 위치도 가져올 수 없습니다");
          setIsGettingLocation(false);
        }
      } catch (ipError) {
        console.error("📍 IP 기반 위치 가져오기 실패:", ipError);
        setIsGettingLocation(false);
      }
    };

    if (!("geolocation" in navigator)) {
      // Geolocation 미지원 시 IP fallback
      await tryIPFallback();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log(`📍 GPS 위치: ${latitude}, ${longitude}`);
        setLocationAndMoveMap(latitude, longitude);
      },
      async (error) => {
        console.warn("📍 GPS 위치 가져오기 실패:", error.message);
        // GPS 실패 시 IP fallback
        await tryIPFallback();
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
    );
  };

  // 간단한 거리 계산 (km)
  const calculateMapDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  return (
    <div ref={rootRef} className="h-full w-full relative">
      <MapErrorBoundary
        boundary="map-view"
        fallback={
          <div className="h-full w-full flex flex-col items-center justify-center gap-2 bg-gray-50 px-4 text-center">
            <MapPin className="h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-700">
              {t("map.errors.mapLoadError")}
            </p>
            <p className="text-xs text-gray-500 max-w-xs">
              {t("map.errors.mapLoadErrorDescription")}
            </p>
          </div>
        }
      >
        <MapsApiProvider>
          <Map
            reuseMaps
            mapId={GOOGLE_MAPS_MAP_ID}
            defaultCenter={center}
            defaultZoom={initialViewZoom}
            zoomControl={false}
            fullscreenControl={false}
            mapTypeControl={false}
            streetViewControl={false}
            clickableIcons={false}
            gestureHandling="greedy"
            scrollwheel={true}
            cameraControl={false}
            style={{ width: "100%", height: "100%" }}
            onCameraChanged={handleMapChange}
            onIdle={(event) => {
              // 프로그램 이동이 끝나 정착한 자리를 다음 이동 판정의 기준점으로 삼는다.
              if (programmaticMoveUntilRef.current !== 0) {
                const settled = mapInstance?.getCenter();
                if (settled) {
                  anchorCenterRef.current = {
                    lat: settled.lat(),
                    lng: settled.lng(),
                  };
                }
              }
              // 카메라가 멈췄으니 프로그램 이동 구간도 끝. 이후 움직임은 사용자 것이다.
              programmaticMoveUntilRef.current = 0;
              // 멈춘 자리의 viewport 를 기록 — "핀이 화면 밖인가" 판정의 입력.
              handleMapIdle(event);
              // 첫 카메라·명소 이동 뒤 한 번 — 화면에 핀이 없으면 가까운 지점까지 넓힌다.
              if (
                isStationVariant &&
                autoRevealPendingRef.current &&
                revealNearestStations("ifNoneVisible")
              ) {
                autoRevealPendingRef.current = false;
              }
            }}
            onDragstart={() => {
              // 사용자 제스처가 시작되면 프로그램 이동 구간은 그 자리에서 끝난다 —
              // 목표와 같은 자리로의 프로그램 호출은 idle 을 못 내 플래그가 남는다.
              programmaticMoveUntilRef.current = 0;
              autoRevealPendingRef.current = false;
              // 부드러운 확대 도중 지도를 잡으면 그 자리에서 멈춘다.
              cancelCameraTween();
            }}
            onClick={() => {
              onMapClick?.();
            }}
          >
            <MapInstanceCapture onReady={setMapInstance} />

            {/* Render cluster markers */}
            {clusters.map((cluster) => {
              const selected = isStationVariant
                ? cluster.products.some((p) => isStationEmphasized(p.id))
                : cluster.products.some((p) =>
                    selectedSpotKeySet.has(markerSpotKey(p)),
                  );
              return (
                <AdvancedMarker
                  key={cluster.id}
                  // 핀을 Maps 가 인정하는 클릭 대상으로 등록한다(gmpClickable).
                  // 클릭은 안쪽 div 의 React onClick 이 받는데, 이 표시가 없으면 Maps 는
                  // 핀을 상호작용 대상으로 모르고 그 자리 클릭을 **지도 click 으로 합성**
                  // 한다(76ms 뒤, isTrusted:false — 09-23 실화면 로그). 그러면 "지도 빈
                  // 곳 클릭 = 목록 범위 해제"가 핀이 방금 건 범위를 곧바로 풀어, 한 번
                  // 클릭은 무시되고 더블클릭에서만 목록이 바뀌었다. 합성 이벤트는 원본과
                  // 다른 객체라 받는 쪽에서 가려낼 방법이 없다 — 합성 자체를 막아야 한다.
                  clickable
                  position={{ lat: cluster.lat, lng: cluster.lng }}
                  anchorTop="-100%"
                  zIndex={selected ? 1000 : undefined}
                >
                  {/* 핀 생김새는 두 variant가 **같은 컴포넌트**를 쓴다 —
                      지점 허브만의 흰 알약을 따로 두면 같은 지도 어휘가 둘로
                      갈라진다. 다른 건 클릭 뿐이다. */}
                  <ClusterMarker
                    cluster={cluster}
                    selected={selected}
                    preciseIcon
                    hidePrice
                    // 몇 곳이 모였는지 — 일반 검색·오토바이·캠핑 허브 공통(10-01). 일반
                    // 검색은 서로 다른 좌표가 2곳 이상일 때만(한 자리에 상품만 여럿이면
                    // 아이콘 겹침이 이미 "여럿"을 말하고, "1곳"은 어색하다).
                    showCount={isStationVariant || (cluster.spotCount ?? 1) > 1}
                    onClick={
                      isStationVariant
                        ? () => {
                            // 지점 클러스터는 모달을 열지 않는다 — 2단계 더
                            // 당겨서 지점들이 갈라지게 하는 것이 허브의 조작
                            // 어휘다(설계 §3-3). 이미 충분히 당겼는데 몇 개 안
                            // 남은 덩어리는 더 당겨도 갈라지지 않으므로 그
                            // 자리에서 낱개 핀으로 펼친다.
                            const currentZoom = mapInstance?.getZoom() ?? zoom;
                            if (
                              shouldExpandStationCluster(
                                cluster.count,
                                currentZoom,
                              )
                            ) {
                              setExpandedStationIds((prev) => {
                                const next = new Set(prev);
                                cluster.products.forEach((p) =>
                                  next.add(String(p.id)),
                                );
                                return next;
                              });
                              return;
                            }
                            if (!mapInstance) return;
                            mapInstance.setCenter({
                              lat: cluster.lat,
                              lng: cluster.lng,
                            });
                            mapInstance.setZoom(currentZoom + 2);
                          }
                        : () => handleClusterClick(cluster)
                    }
                    label={
                      isStationVariant || (cluster.spotCount ?? 1) > 1
                        ? t("stationClusterLabel", {
                            count: cluster.spotCount ?? cluster.count,
                          })
                        : t("clusterProducts", { count: cluster.count })
                    }
                  />
                </AdvancedMarker>
              );
            })}

            {/* Render single markers */}
            {singleMarkers.map((product) => {
              // 리스트 호버(원본 id)와 목록 범위로 고른 자리 둘 다 선택으로 본다.
              // station variant 는 id 를 그대로 비교한다(접미사 절단 없음).
              const selected = isStationVariant
                ? isStationEmphasized(product.id)
                : (highlightedProductId != null &&
                    baseProductId(product.id) ===
                      baseProductId(highlightedProductId)) ||
                  selectedSpotKeySet.has(markerSpotKey(product));
              return (
                <AdvancedMarker
                  key={product.id}
                  // 클러스터 마커와 같은 이유 — 위 주석 참고.
                  clickable
                  position={{
                    lat: product.location.lat,
                    lng: product.location.lng,
                  }}
                  anchorTop="-100%"
                  zIndex={selected ? 1000 : undefined}
                >
                  <Marker
                    product={product}
                    markerStyle={markerStyle}
                    selected={selected}
                    preciseIcon
                    hidePrice
                    onMouseEnter={() => onMarkerHover?.(product.id)}
                    onMouseLeave={() => onMarkerHover?.(null)}
                    onClick={
                      isStationVariant
                        ? () => onMarkerClick?.(String(product.id))
                        : () => {
                            onSpotSelect?.(spotScopeOf([product]));
                            requestCamera({
                              kind: "reveal",
                              point: {
                                lat: product.location.lat,
                                lng: product.location.lng,
                              },
                            });
                          }
                    }
                  />
                </AdvancedMarker>
              );
            })}

            {/* Render current location marker — CENTER anchor so the dot
                lands exactly on the user's coordinate, not above it. */}
            {currentLocation && (
              <AdvancedMarker
                position={{
                  lat: currentLocation.lat,
                  lng: currentLocation.lng,
                }}
                anchorLeft="-50%"
                anchorTop="-50%"
              >
                <CurrentLocationMarker />
              </AdvancedMarker>
            )}
            {/* 명소 — 라벨 알약 아래 점, 점이 정확히 명소 좌표에 오도록 아래 가운데 기준.
                지점 핀보다 **아래**(zIndex -1): 명소는 기준점일 뿐, 눌러야 하는 건 핀이다(09-23). */}
            {landmark && (
              <AdvancedMarker
                position={{ lat: landmark.lat, lng: landmark.lng }}
                anchorLeft="-50%"
                anchorTop="-100%"
                zIndex={-1}
              >
                {/* 주황 테마(09-28) — 연주황 상품 핀과 겹쳐도 "기준 명소"로 구분된다 */}
                <div className="pointer-events-none flex flex-col items-center gap-1.5">
                  <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(0,0,0,0.16)]">
                    <MapPinIcon weight="fill" className="size-3" />
                    {landmark.label}
                  </span>
                  <span className="h-3.5 w-3.5 rounded-full bg-orange-500 shadow-[0_0_0_4px_#fff,0_2px_6px_rgba(0,0,0,0.25)]" />
                </div>
              </AdvancedMarker>
            )}
          </Map>
        </MapsApiProvider>
      </MapErrorBoundary>

      {/* 모바일: 내 위치 버튼 (좌측 하단) */}
      <div className="absolute bottom-4 left-4 z-10 sm:hidden flex flex-col items-start gap-2">
        {/* 핀이 화면 밖일 때만 — "지점이 있는 곳으로 이동".
            "현 위치"로 한국에 간 사용자를 일본 지점으로 되돌리는 유일한 출구다. */}
        {markersOffscreen && (
          <button
            onClick={fitAllMarkers}
            className="animate-jump-pin bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg pl-1 pr-3.5 py-1 flex items-center gap-2 whitespace-nowrap"
            title={t("map.jumpToPins")}
            aria-label={t("map.jumpToPins")}
          >
            {/* 흰 원판의 지름(32px) + pl-1(4px) 이 나침반 버튼의 반지름(20px)과
                맞아떨어져, 알약 아이콘과 아래 나침반이 같은 세로선에 선다. */}
            <span className="h-8 w-8 rounded-full bg-white flex items-center justify-center shrink-0">
              <MapPinIcon size={18} weight="regular" className="text-orange-500" />
            </span>
            <span className="text-[13px] font-semibold">
              {t("map.jumpToPinsShort")}
            </span>
          </button>
        )}
        <button
          onClick={handleGetCurrentLocation}
          disabled={isGettingLocation}
          className="bg-white hover:bg-gray-50 p-2.5 rounded-full shadow-lg transition-all disabled:cursor-not-allowed"
          title={t("map.myLocation")}
        >
          {isGettingLocation ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Compass
              className={`w-5 h-5 ${
                currentLocation ? "text-blue-500" : "text-gray-600"
              }`}
            />
          )}
        </button>
      </div>

      {/* 모바일: 이 지역에서 검색 버튼 (하단 중앙, 텍스트 포함) */}
      {/* areaSearchPending 을 함께 보는 이유 — 누르는 순간 hasMapMoved 가 false 가 되므로
          그것만 보면 결과를 받는 동안 스피너가 한 프레임도 보이지 않는다. */}
      {(hasMapMoved || areaSearchPending) && (
        <button
          onClick={handleSearchCurrentLocation}
          disabled={areaSearchPending}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 sm:hidden bg-white hover:bg-gray-50 text-gray-900 px-4 py-2.5 rounded-full shadow-lg transition-all flex items-center gap-2 font-medium text-sm disabled:cursor-not-allowed"
          title={t("searchHere")}
        >
          {areaSearchPending ? (
            <LoadingSpinner size="sm" />
          ) : (
            <RotateCw className="w-4 h-4 text-orange-500" />
          )}
          {t("searchHere")}
        </button>
      )}

      {/* 데스크탑: 이 지역에서 검색 버튼 (상단 중앙) */}
      {(hasMapMoved || areaSearchPending) && (
        <button
          onClick={handleSearchCurrentLocation}
          disabled={areaSearchPending}
          className="hidden sm:flex absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white hover:bg-gray-50 text-gray-900 px-4 py-2 rounded-full shadow-lg transition-all items-center gap-2 font-medium text-sm disabled:cursor-not-allowed"
          title={t("searchHere")}
        >
          {areaSearchPending ? (
            <LoadingSpinner size="sm" />
          ) : (
            <RotateCw className="w-4 h-4 text-orange-500" />
          )}
          {t("searchHere")}
        </button>
      )}

      {/* 데스크탑: Zoom Controls & Current Location Button - Bottom Right */}
      <div className="hidden sm:flex absolute bottom-4 right-4 z-10 flex-col items-end gap-2">
        {/* Zoom In/Out Buttons */}
        <div className="flex flex-col bg-white rounded-full shadow-lg overflow-hidden">
          <button
            onClick={() => {
              if (mapInstance) {
                const currentZoom = mapInstance.getZoom() || 12;
                mapInstance.setZoom(currentZoom + 1);
              }
            }}
            className="p-3 hover:bg-gray-50 transition-all border-b border-gray-100"
            title="Zoom in"
          >
            <Plus className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={() => {
              if (mapInstance) {
                const currentZoom = mapInstance.getZoom() || 12;
                mapInstance.setZoom(currentZoom - 1);
              }
            }}
            className="p-3 hover:bg-gray-50 transition-all"
            title="Zoom out"
          >
            <Minus className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* 핀이 화면 밖일 때만 — "지점이 있는 곳으로 이동".
            "현 위치"로 한국에 간 사용자를 일본 지점으로 되돌리는 유일한 출구다. */}
        {markersOffscreen && (
          <button
            onClick={fitAllMarkers}
            className="animate-jump-pin bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg pl-1 pr-3.5 py-1.5 flex items-center gap-2 whitespace-nowrap"
            title={t("map.jumpToPins")}
            aria-label={t("map.jumpToPins")}
          >
            <span className="h-8 w-8 rounded-full bg-white flex items-center justify-center shrink-0">
              <MapPinIcon size={18} weight="regular" className="text-orange-500" />
            </span>
            <span className="text-[13px] font-semibold">
              {t("map.jumpToPinsShort")}
            </span>
          </button>
        )}

        {/* Current Location Button */}
        <button
          onClick={handleGetCurrentLocation}
          disabled={isGettingLocation}
          className="bg-white hover:bg-gray-50 p-3 rounded-full shadow-lg transition-all disabled:cursor-not-allowed"
          title={t("map.myLocation")}
        >
          {isGettingLocation ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Compass
              className={`w-5 h-5 ${
                currentLocation ? "text-blue-500" : "text-gray-600"
              }`}
            />
          )}
        </button>
      </div>

    </div>
  );
}

// 메모이제이션으로 불필요한 재렌더링 방지
export default memo(MapView);
